import {
  FilterType,
  FiltersState,
  ProductData,
  LiteratureDocumentListingData,
} from '@types';
import { Injectable, OnDestroy } from '@angular/core';

import get from 'lodash/get';
import { Subject, Subscription, Observable } from 'rxjs';
import {
  BaseFilter as Filter,
  FundFilterService,
} from '../../products/fund-listing/services/fund-filter.service';
import { isLiteratureCodeWithSpace } from '@utils/text/string-utils';

@Injectable()
export abstract class FilterStateManagerService implements OnDestroy {
  private subject: Subject<FiltersState>;
  protected state: FiltersState = {
    primaryFilters: [],
    additionalFilters: [],
    searchKeyword: {
      label: '',
      selected: [],
    },
    morningStarFilter: {
      label: '',
      selected: [],
    },
    clientUseFilter: {
      label: '',
      selected: [],
    },
  };
  private products: ProductData[] | LiteratureDocumentListingData[];
  protected filtersWithValues = new Map<Filter, Map<string, number>>();
  protected defaultSelectedFilters = new Map<Filter, string[]>();
  protected selectedPreFilter: string;
  private defaultSelection = true;
  private stateSubscription: Subscription;
  private dataSubscription: Subscription;

  constructor(private filterService: FundFilterService) {
    this.subject = new Subject();
  }

  /**
   * Extract filters from decorated config and assign the values to the state.
   * @param componentConfiguration dynamic component configuration
   */
  public mapState() {
    this.dataSubscription = this.filterService
      .getData()
      .subscribe(([dataType, data]) => {
        switch (dataType) {
          case 'initialList':
            this.init(data);
            break;
          case 'initialListPPSS':
            this.initDataforLunr(data);
            break;
          case 'initialListLiterature':
            this.initDataforLunr(data);
            break;
          case 'filteredList':
            // uncomment line below if counts are implemented.
            // this.updateCounts(data);
            this.sendNext('countUpdated');
            break;
          case 'requery':
            this.resetDropdowns();
            this.sendNext('requery');
            break;
        }
      });
  }

  public getSelectedFilters() {
    const selectedPrimaryfilters = this.state.primaryFilters
      .filter((filter) => filter.selected.length > 0)
      .map((filt) => filt.label + ':' + filt.selected);
    const selectedAdditionalfilters = this.state.additionalFilters
      .filter((filter) => filter.selected.length > 0)
      .map((filt) => filt.label + ':' + filt.selected);
    const selectedSearchQuery: string[] = this.state.searchKeyword?.selected
      ? [
          this.state.searchKeyword?.selected[0]
            ? 'searchQuery:' + this.state.searchKeyword.selected
            : null,
        ]
      : [null];
    const selectedfilters = [
      ...selectedPrimaryfilters,
      ...selectedAdditionalfilters,
      ...selectedSearchQuery,
    ];
    return selectedfilters.length ? selectedfilters.join(';') : null;
  }

  /**
   * Called when product data loads for first time.
   * @param data initial product data
   * @param componentConfiguration dynamic component configuration
   */
  private init(data: ProductData[] | LiteratureDocumentListingData[]) {
    this.products = data;
    // this.updateCounts(filteredProducts) To-Do: Need fix!
    this.createFilters();
    if (this.selectedPreFilter) {
      this.filterOnPreFilters(this.selectedPreFilter);
    } else {
      this.filterOnDefaultFilters();
    }
  }

  private initDataforLunr(data: ProductData[]) {
    this.products = data;
    // this.updateCounts(filteredProducts) To-Do: Need fix!
    this.createFilters();
    if (this.selectedPreFilter) {
      this.filterOnPreFilters(this.selectedPreFilter, true);
    } else if (this.state.searchKeyword?.selected?.length) {
      this.filterLunr();
    } else {
      this.filterOnDefaultFilters();
    }
  }

  protected abstract createFilters(): void;

  /** preFilter handler, works on url filtering
   * supports filtering based on query
   * @param path param which comes from route
   */
  filterOnPreFilters(selectedFilters: string, lunrSearch?: boolean) {
    let isSelected = false;
    const prefilters: Map<string, string[]> = new Map<string, string[]>();
    if (selectedFilters) {
      const filters = selectedFilters.split(';');
      filters.forEach((element) => {
        const sel = element.split(':');
        prefilters.set(sel[0], sel[1]?.split(','));
      });
    }
    this.state.primaryFilters.forEach((primaryFilterItem) => {
      if (prefilters.has(primaryFilterItem.label)) {
        primaryFilterItem.selected = prefilters.get(primaryFilterItem.label);
        isSelected = true;
      }
    });
    this.state.additionalFilters.forEach((additionalFilterItem) => {
      if (prefilters.has(additionalFilterItem.label)) {
        additionalFilterItem.selected = prefilters.get(
          additionalFilterItem.label
        );
        isSelected = true;
      }
    });
    if (prefilters.has('searchQuery')) {
      this.state.searchKeyword.selected = prefilters.get('searchQuery');
      isSelected = true;
    }
    if (isSelected && lunrSearch) {
      this.filterLunr();
    } else if (isSelected) {
      this.filter();
    } else {
      this.filterOnDefaultFilters();
    }
  }

  /**
   * Handle filter against any selection/search made,
   * by fetching details from state.
   */
  protected filterLunr(requery?: boolean) {
    const selectedFilter = this.generateSelectedFilterWithoutSearch();

    // Check if map is empty, then use default one.
    let searchKeyword;
    if (this.state.searchKeyword?.selected) {
      searchKeyword = this.state.searchKeyword?.selected[0].trim();
    }
    if (selectedFilter?.size === 0 && !searchKeyword) {
      this.filterOnDefaultFilters();
    } else {
      this.defaultSelection = false;
      requery = requery && selectedFilter?.size >= 1 && !!searchKeyword;
      const searchFilter = { ...this.state.searchKeyword };
      if (searchKeyword) {
        searchFilter.selected = this.normalizeSearch(searchKeyword);
      }
      this.filterService.filterWithLunr(
        { selectedFilters: selectedFilter },
        searchFilter,
        requery
      );
    }
  }
  /**
   * Return filter type object against inputs from decorated config & feed.
   * @param filterListItem various filter type from decorated config, like in - primary/additional
   */
  protected createFilterType(filterListItem: Filter): FilterType {
    const valuesWithCount = this.sortFilterValues(filterListItem);
    return {
      label: filterListItem.key,
      options: valuesWithCount.map(([value, count]) => {
        return { value, count };
      }),
      selected: [],
      type: filterListItem,
    };
  }
  /**
   * provide the default sorting on filter values.
   * @param filterListItem various filter type from decorated config, like in - primary/additional
   */
  protected sortFilterValues(
    filterListItem: Filter,
    reverse?: boolean
  ): [string, number][] {
    return Array.from(
      this.filtersWithValues.get(filterListItem).entries()
    ).sort((a, b) => {
      if (reverse) {
        return a[0] > b[0] ? -1 : 1;
      }
      return a[0] < b[0] ? -1 : 1;
    });
  }

  /**
   * Handle filter on product with default selected filter.
   */
  protected filterOnDefaultFilters() {
    this.defaultSelection = true;
    if (this.defaultSelectedFilters.size === 0) {
      this.defaultSelection = false;
      this.sendNext('initialListEmitted');
    }
    this.filterService.filter({ selectedFilters: this.defaultSelectedFilters });
  }

  /**
   * Format the product data keys to its filter as string[],
   * but only for those fields that are in decorated config as primary & additional filter.
   * @param filters combined filter i.e., primary & additional from decorated config
   */
  protected formatProductDataByFilters(filters: Filter[]) {
    filters.forEach((filter) => {
      // todochild filter
      this.filtersWithValues.set(filter, new Map());
    });
    if (this.products?.length) {
      this.assignFiltersWithValues(this.products, this.filtersWithValues);
    }
  }

  /**
   * Assign values to filter types as a string array.
   */
  private assignFiltersWithValues(
    products: ProductData[] | LiteratureDocumentListingData[],
    filtersWithValues: Map<Filter, Map<string, number>>
  ) {
    products.forEach(
      (productData: ProductData | LiteratureDocumentListingData) => {
        for (const [filter, values] of filtersWithValues.entries()) {
          for (const path of filter.paths) {
            const value: string[] | string = get(productData, path, null);
            if (value && Array.isArray(value)) {
              for (const resultValue of value) {
                if (
                  !filter.excludeValuesFromFilter.includes(
                    resultValue.toLocaleUpperCase()
                  )
                ) {
                  this.setValues(resultValue, values);
                }
              }
            } else if (typeof value === 'string') {
              if (
                !filter.excludeValuesFromFilter.includes(
                  value.toLocaleUpperCase()
                )
              ) {
                this.setValues(value, values);
              }
            }
          }
        }
      }
    );
  }

  setValues(value: string, values: Map<string, number>) {
    const finalValue = value?.trim();
    if (finalValue && finalValue !== '' && finalValue !== '-') {
      if (values.has(finalValue)) {
        values.set(finalValue, values.get(finalValue) + 1);
      } else {
        values.set(finalValue, 1);
      }
    }
  }

  /**
   * Update the count of the rows against filtered options.
   * @param filteredProducts current table rows
   */
  private updateCounts(
    filteredProducts: ProductData[] | LiteratureDocumentListingData[]
  ) {
    const filtersWithValues = this.defaultSelection
      ? this.filtersWithValues
      : new Map<Filter, Map<string, number>>();
    if (!this.defaultSelection) {
      for (const [filter, values] of this.filtersWithValues.entries()) {
        const optionsWithValue = new Map<string, number>();
        filtersWithValues.set(filter, optionsWithValue);
        for (const [value, count] of values.entries()) {
          optionsWithValue.set(value, 0);
        }
      }
      this.assignFiltersWithValues(filteredProducts, filtersWithValues);
    }

    this.state.primaryFilters.forEach((filterType: FilterType) => {
      const valueWithCounts = this.sortFilterValues(filterType.type);
      filterType.options = valueWithCounts.map(([value, count]) => {
        return { value, count };
      });
    });

    this.state.additionalFilters.forEach((filterType: FilterType) => {
      const valueWithCounts = this.sortFilterValues(filterType.type);
      filterType.options = valueWithCounts.map(([value, count]) => {
        return { value, count };
      });
    });
    // To-Do, iterate on products.
    // this.sendNext('countUpdated');
  }

  /**
   * Filter change handler.
   * @param filterClass the filter class with either of the values from 'primary'/'additional'.
   * @param selectedLabel the label for which selection made
   * @param selectedValues the label value(s) against the label
   * @param withRequery Optional for requery logic
   */
  updateFilter(
    filterClass: string,
    selectedLabel: string,
    selectedValues: string[],
    withRequery?: boolean
  ) {
    if (filterClass === 'primary') {
      this.state.primaryFilters.forEach((filterTypeObj: FilterType) => {
        if (filterTypeObj.label === selectedLabel) {
          if (selectedValues.indexOf('All') > -1) {
            filterTypeObj.selected = ['All'];
          } else {
            filterTypeObj.selected = selectedValues;
          }
        }
      });
    } else if (filterClass === 'additional') {
      this.state.additionalFilters.forEach((filterTypeObj: FilterType) => {
        if (filterTypeObj.label === selectedLabel) {
          if (selectedValues.indexOf('All') > -1) {
            filterTypeObj.selected = ['All'];
          } else {
            filterTypeObj.selected = selectedValues;
          }
        }
      });
    }
    if (withRequery || this.state.searchKeyword?.selected?.length) {
      this.filterLunr(withRequery);
    } else {
      this.filter();
    }
  }

  /**
   * Filter by search handler.
   * @param value input text
   * @param withRequery optional boolean for requery logic
   */
  searchByKeyword(value: string, withRequery?: boolean) {
    this.state.searchKeyword.selected = [value];
    this.filterLunr(withRequery);
  }

  /**
   * Filter by morning star rating.
   * @param value input text
   */
  filterByMorningstarRating(value: string) {
    if (value.trim() === '') {
      this.state.morningStarFilter.selected = [];
    } else {
      this.state.morningStarFilter.selected = [value.trim()];
    }
    this.filter();
  }

  /**
   * Handle filter against any selection/search made,
   * by fetching details from state.
   *  @param withRequery boolean optional for requery logic
   */
  protected filter() {
    const selectedFilter = this.generateSelectedFilterMappingFromState();

    // Check if map is empty, then use default one.
    if (selectedFilter?.size === 0) {
      this.filterOnDefaultFilters();
    } else {
      this.defaultSelection = false;
      this.filterService.filter({ selectedFilters: selectedFilter });
    }
  }

  /**
   * Reset the filter selection and state accordingly.
   */
  resetFilters() {
    this.resetDropdowns();
    this.state.searchKeyword.selected = [];
    this.state.morningStarFilter.selected = [];
    this.state.clientUseFilter.selected = [];

    this.filterOnDefaultFilters();
  }

  /**
   * Reset dropdown filter state only.
   */
  resetDropdowns() {
    this.state.primaryFilters.forEach((filter: FilterType) => {
      filter.selected = [];
    });

    this.state.additionalFilters.forEach((filter: FilterType) => {
      filter.selected = [];
    });
  }

  /**
   * Generate expected filter selection mapping from state.
   * Traverse through primary, additional & search filter and return cumulative selections as Map.
   */
  private generateSelectedFilterMappingFromState(): Map<Filter, string[]> {
    const selectedFilter = new Map<Filter, string[]>();

    // Extract selections from primary filter, if any.
    this.state.primaryFilters.forEach((filter: FilterType) => {
      if (filter.selected?.length > 0) {
        if (filter.selected.indexOf('All') < 0) {
          selectedFilter.set(filter.type, filter.selected);
        }
      }
    });

    // Extract selections from additional filter, if any.
    this.state.additionalFilters.forEach((filter: FilterType) => {
      if (filter.selected?.length > 0) {
        selectedFilter.set(filter.type, filter.selected);
      }
    });

    // Extract input from search filter, if any.
    if (this.state?.searchKeyword?.selected?.length > 0) {
      selectedFilter.set(
        this.state.searchKeyword.type,
        this.normalizeSearch(this.state.searchKeyword.selected[0])
      );
    }

    // Extract input from morning star filter, if any.
    if (this.state.morningStarFilter?.selected?.length > 0) {
      selectedFilter.set(
        this.state.morningStarFilter.type,
        this.state.morningStarFilter.selected
      );
    }

    // Extract input from client use filter, if any.
    if (this.state.clientUseFilter?.selected?.length > 0) {
      selectedFilter.set(
        this.state.clientUseFilter.type,
        this.state.clientUseFilter.selected
      );
    }

    // Return the cumulative filter selections.
    return selectedFilter;
  }

  /**
   * Generate expected filter selection mapping from state.
   * Traverse through primary, additional and return cumulative selections as Map.
   */
  protected generateSelectedFilterWithoutSearch(): Map<Filter, string[]> {
    const selectedFilter = new Map<Filter, string[]>();

    // Extract selections from primary filter, if any.
    this.state.primaryFilters.forEach((filter: FilterType) => {
      if (filter.selected?.length > 0) {
        if (filter.selected.indexOf('All') < 0) {
          selectedFilter.set(filter.type, filter.selected);
        }
      }
    });

    // Extract selections from additional filter, if any.
    this.state.additionalFilters.forEach((filter: FilterType) => {
      if (filter.selected?.length > 0) {
        selectedFilter.set(filter.type, filter.selected);
      }
    });

    // Extract input from morning star filter, if any.
    if (this.state.morningStarFilter?.selected?.length > 0) {
      selectedFilter.set(
        this.state.morningStarFilter.type,
        this.state.morningStarFilter.selected
      );
    }

    // Extract input from client use filter, if any.
    if (this.state.clientUseFilter?.selected?.length > 0) {
      selectedFilter.set(
        this.state.clientUseFilter.type,
        this.state.clientUseFilter.selected
      );
    }

    // Return the cumulative filter selections.
    return selectedFilter;
  }

  // This function add normalization rules for search optimization.
  private normalizeSearch(searchKeyword: string): string[] {
    let searchArray = [searchKeyword];

    // 1: Add a new string in search array by Replacing spaces with hyphon if this is a litcode.
    if (isLiteratureCodeWithSpace(searchKeyword)) {
      searchArray = [searchKeyword.replace(' ', '-'), searchKeyword];
    }

    // 2: This is a temperaory fix for WDE-4210 requirement.To improve the smartness of search filter we will work on
    // another solution under WDE-4610.

    if (searchKeyword.trim().toLowerCase() === 'tax free income') {
      searchArray = ['tax-free income', searchKeyword];
    }

    // 3: Code to check if string consist of standard words in plural form and will replace with singular.
    // Todos:: The standardWords can be made configurable in future.
    const standardWords = ['form', 'fund', 'document'];
    const matchedWord = standardWords.filter((word) => {
      return searchKeyword.toLowerCase().match(word + 's');
    });

    if (matchedWord?.length) {
      searchArray = [
        searchKeyword,
        searchKeyword
          .toLowerCase()
          .replace(matchedWord[0] + 's', matchedWord[0]),
      ];
    }

    return searchArray;
  }

  /**
   * Return state subject with a subscriber value.
   * @param subscriber Subscribed object
   */
  subscribe(subscriber) {
    this.stateSubscription = this.subject.subscribe(subscriber);
  }

  getFilterState(): Observable<FiltersState> {
    return this.subject.asObservable();
  }

  /**
   * Unsubscribe the state subject.
   */
  unsubscribe() {
    this.stateSubscription?.unsubscribe();
  }

  /**
   * Provides the next observer value to the subscriber.
   * @param actionName Current action name
   */
  protected sendNext(actionName: string) {
    this.state.lastAction = actionName;
    this.subject.next(this.state);
  }

  ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe();
    this.dataSubscription?.unsubscribe();
  }
}
